import { apiClient } from '_app'
import { CancelToken } from 'axios'
import { ApiResponse, VehicleDto } from 'dtos'
import { ClusterShipperDto } from 'dtos'
import { ClusterShipperThirdPartyConversionDeliveryDto } from 'dtos'
import { ClusterShippersParameters } from 'parameters'
import { base64ToArrayBuffer, URIQuery } from 'utils'

export const getClusterShippers = (parameters: ClusterShippersParameters) =>
  apiClient.get<ApiResponse<ClusterShipperDto[]>>('api/cluster-shippers' + URIQuery({ ...parameters })).then(({ data }) => data)

export const getClusterShipper = (clusterShipperGuid: string) =>
  apiClient
    .get<ApiResponse<ClusterShipperDto>>(`/api/cluster-shippers/${encodeURIComponent(clusterShipperGuid)}`)
    .then(response => response.data)

export const getClusterShippersThirdPartyConversionDeliveryInformation = (clusterShipperGuid: string) =>
  apiClient
    .get<ApiResponse<ClusterShipperThirdPartyConversionDeliveryDto>>(
      `/api/cluster-shippers/${encodeURIComponent(clusterShipperGuid)}/cluster-shippers-third-party-conversion-delivery-photo`
    )
    .then(response => response.data)

export const updateClusterShippersThirdPartyConversionDeliveryInformation = (
  clusterShipperGuid: string,
  values: ClusterShipperThirdPartyConversionDeliveryDto
) => {
  const payload = new FormData()

  payload.append(
    'clusterShipperThirdPartyConversionDeliveryPhotoFile',
    values.clusterShipperThirdPartyConversionDeliveryPhotoFile!
  )

  return apiClient<ApiResponse<ClusterShipperThirdPartyConversionDeliveryDto>>({
    data: payload,
    headers: { 'Content-Type': 'multipart/form-data' },
    method: 'put',
    url: `/api/cluster-shippers/${encodeURIComponent(
      clusterShipperGuid
    )}/cluster-shippers-third-party-conversion-delivery-photo`
  })
    .then(response => response.data)
    .catch(e => {
      throw e
    })
}

export const createClusterShipper = (values: ClusterShipperDto) =>
  apiClient.post<ApiResponse<ClusterShipperDto>>('/api/cluster-shippers', values).then(response => response.data)

export const updateClusterShipper = (values: ClusterShipperDto) =>
  apiClient.put<ApiResponse<ClusterShipperDto>>('/api/cluster-shippers', values).then(response => response.data)

export const getClusterShipperVehicles = (clusterShipperGuid: string, parameters: ClusterShippersParameters) =>
  apiClient
    .get<ApiResponse<VehicleDto[]>>(
      `/api/cluster-shippers/${encodeURIComponent(clusterShipperGuid)}/vehicles` + URIQuery({ ...parameters })
    )
    .then(response => response.data)

export const addVehiclesToClusterShipper = (clusterShipperGuid: string, values: VehicleDto[]) =>
  apiClient
    .post<ApiResponse<ClusterShipperDto>>(`/api/cluster-shippers/${encodeURIComponent(clusterShipperGuid)}/vehicles`, values)
    .then(response => response.data)

export const removeVehiclesFromClusterShipper = (clusterShipperGuid: string, values: VehicleDto[]) =>
  apiClient
    .delete<ApiResponse<ClusterShipperDto>>(`/api/cluster-shippers/${encodeURIComponent(clusterShipperGuid)}/vehicles`, {
      data: values
    })
    .then(response => response.data)

export const getClusterShipperThirdPartyConversionDeliveryPhotoBase64 = (clusterShipperGuid: string, fileName: string) =>
  apiClient
    .get<string>(
      `/api/cluster-shippers/${encodeURIComponent(
        clusterShipperGuid
      )}/cluster-shippers-third-party-conversion-delivery-photo/${encodeURIComponent(fileName)}`
    )
    .then(response => response.data)

export const getClusterShipperThirdPartyConversionDeliveryPhoto = (clusterShipperGuid: string) =>
  apiClient
    .get<ApiResponse<ClusterShipperThirdPartyConversionDeliveryDto>>(
      `api/cluster-shippers/${encodeURIComponent(clusterShipperGuid)}/cluster-shippers-third-party-conversion-delivery-photo`
    )
    .then(response => response.data)

export const getClusterShippersThirdPartyConversionDeliveryFile = (clusterShipperGuid: string, fileName: string) =>
  apiClient
    .get(
      `api/cluster-shippers/${encodeURIComponent(
        clusterShipperGuid
      )}/cluster-shippers-third-party-conversion-delivery-photo${fileName}`
    )
    .then(response => {
      const buffer = base64ToArrayBuffer(response.data)
      const file = new Blob([buffer], { type: 'image/png' })
      const fileUrl = window.URL.createObjectURL(file)
      const _newTab = window.open(fileUrl)
    })

export const updateClusterShipperThirdPartyConversionDeliveryPhoto = (
  clusterShipperGuid: string,
  values: ClusterShipperThirdPartyConversionDeliveryDto
) => {
  const payload = new FormData()

  if (values.clusterShipperThirdPartyConversionDeliveryPhotoFile) {
    payload.append(
      'clusterShipperThirdPartyConversionDeliveryPhotoFile',
      values.clusterShipperThirdPartyConversionDeliveryPhotoFile
    )
  }

  return apiClient<ApiResponse<ClusterShipperThirdPartyConversionDeliveryDto>>({
    data: payload,
    headers: { 'Content-Type': 'multipart/form-data' },
    method: 'put',
    url: `/api/cluster-shippers/${encodeURIComponent(
      clusterShipperGuid
    )}/cluster-shippers-third-party-conversion-delivery-photo`
  })
    .then(response => response.data)
    .catch(e => {
      throw e
    })
}

export const getClusterShipperPdf = (clusterShipperGuid: string) =>
  apiClient
    .get(`/api/cluster-shippers/${encodeURIComponent(clusterShipperGuid)}/cluster-shipper-pdf`, {
      responseType: 'blob'
    })
    .then(response => {
      const file = new Blob([response.data], { type: 'application/pdf' })
      const fileUrl = window.URL.createObjectURL(file)
      window.open(fileUrl)
    })

export const getClusterShipperReport = (clusterShipperGuid: string) =>
  apiClient
    .get(`/api/cluster-shippers/${encodeURIComponent(clusterShipperGuid)}/cluster-shipper-report`)
    .then(response => response.data)

export const deleteClusterShipperThirdPartyConversionDeliveryPhoto = (clusterShipperGuid: string) =>
  apiClient
    .delete<ApiResponse<ClusterShipperThirdPartyConversionDeliveryDto>>(
      `/api/cluster-shippers/${encodeURIComponent(clusterShipperGuid)}/cluster-shippers-third-party-conversion-delivery-photo`
    )
    .then(response => response.data)
